import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminPageHeader from '@/components/common/AdminPageHeader';
import { toast } from 'react-toastify';
import useI18n from '@/hooks/useI18n';
import Loading from '@/components/common/Loading';
import { fetchAllUsersAndLinks } from '@/services/userService';
import { formatDisplayDate } from '@/utils/dateUtils';
import { ADMIN_POSITIVE_CHIP } from '@/config/adminChartTheme';
import { useGetAllUsersAndLinksQuery } from '@/hooks/queries/useAdminQueries';

interface Link {
  _id: string;
  originalUrl: string;
  shortUrl: string;
  targetSite: string;
  clickCount: number;
  domain: string;
  subDomain: string;
  prefix: string;
  createdAt: string;
  metaTitle?: string;
  metaDescription?: string;
  metaImageUrl?: string;
  isFavorite?: boolean;
  tags?: string[];
  brand?: string | null;
}

interface UserDetail {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  country: string;
  city: string;
  mobileNumber?: string;
  countryCode?: string;
  role: string;
  brand: string;
  gender?: string;
  occupation?: string;
  postalCode?: string;
  address?: string;
  authProvider: string;
  status: string;
  isWhitelisted: boolean;
  createdAt: string;
  links: Link[];
  totalClicks: number;
  linkCount: number;
  profileImage?: string;
}

const AdminUserDetail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [user, setUser] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [linkSearchTerm, setLinkSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchUserData = async () => {
      if (!userId) return;

      try {
        setLoading(true);
        const response = await fetchAllUsersAndLinks({
          filter: '',
          page: 1,
          limit: 1000,
          sortField: '_id',
          sortOrder: 'desc',
        });

        const foundUser = response.data.find((u: any) => u._id === userId);
        if (foundUser) {
          const totalClicks = foundUser.links?.reduce(
            (sum: number, link: Link) => sum + link.clickCount,
            0,
          );
          setUser({ ...foundUser, totalClicks });
        } else {
          toast.error('User not found');
          navigate('/admin/users');
        }
      } catch (error) {
        console.error('Error fetching user:', error);
        toast.error('Failed to load user details');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [userId, navigate]);

  const handleBack = () => {
    navigate('/admin/users');
  };

  // Filter and sort links
  const filteredLinks = useMemo(() => {
    if (!user?.links) return [];
    return user.links.filter((link) => {
      const searchLower = linkSearchTerm.toLowerCase();
      return (
        link.originalUrl.toLowerCase().includes(searchLower) ||
        link.shortUrl.toLowerCase().includes(searchLower) ||
        link.targetSite.toLowerCase().includes(searchLower)
      );
    });
  }, [user?.links, linkSearchTerm]);

  const sortedLinks = useMemo(() => {
    const sorted = [...filteredLinks];
    sorted.sort((a, b) => {
      return sortOrder === 'asc'
        ? a.clickCount - b.clickCount
        : b.clickCount - a.clickCount;
    });
    return sorted;
  }, [filteredLinks, sortOrder]);

  const paginatedLinks = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedLinks.slice(startIndex, endIndex);
  }, [currentPage, sortedLinks]);

  const totalPages = Math.ceil(sortedLinks.length / itemsPerPage);

  if (loading) {
    return <Loading fullScreen={true} />;
  }

  if (!user) {
    return (
      <div className="p-[24px]">
        <p>User not found</p>
      </div>
    );
  }

  const fullName =
    `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown User';
  const phone =
    user.countryCode && user.mobileNumber
      ? `${user.countryCode} ${user.mobileNumber}`
      : user.mobileNumber || 'N/A';

  const statsData = [
    {
      label: 'Total Links',
      value: user.linkCount || user.links?.length || 0,
      icon: '/assets/linkIcon.svg',
    },
    {
      label: 'Total Clicks',
      value: user.totalClicks || 0,
      icon: '/assets/analyticsIcon.svg',
    },
    {
      label: 'Joined',
      value: formatDisplayDate(user.createdAt),
      icon: '/assets/fi_calendar.svg',
    },
    {
      label: 'Status',
      value: user.status
        ? user.status.charAt(0).toUpperCase() + user.status.slice(1)
        : 'N/A',
      icon: '/assets/check-circle.svg',
    },
  ];

  return (
    <div className="bg-[#FCFCFC] min-h-screen">
      <AdminPageHeader
        title="User Details"
        subtitle="Profile, links and activity overview"
        back={
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-2 text-sm font-medium text-[#9B919D] hover:text-[#3C2479] transition-colors"
          >
            <img src="/assets/leftArrow.svg" alt="" className="w-4 h-4" />
            {t('common.back')}
          </button>
        }
      />

      <div className="p-[24px] flex flex-col gap-[24px]">
        {/* User Information Card */}
        <div className="bg-white border border-[#E5E9EB] rounded-3xl p-[24px]">
          <div className="flex items-start gap-6">
            <img
              src={user.profileImage || '/assets/profileImg.svg'}
              alt={fullName}
              className="w-[80px] h-[80px] rounded-full object-cover border-2 border-[#E5E9EB]"
            />
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-2xl font-semibold text-[#292828]">
                  {fullName}
                </h2>
                {user.isWhitelisted && (
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${ADMIN_POSITIVE_CHIP}`}
                  >
                    Whitelisted
                  </span>
                )}
              </div>
              <p className="text-[#9B919D] text-base mb-4">{user.email}</p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                <div>
                  <span className="text-xs text-[#9B919D]">Phone</span>
                  <p className="text-sm text-[#292828] font-medium">{phone}</p>
                </div>
                {user.country && (
                  <div>
                    <span className="text-xs text-[#9B919D]">Country</span>
                    <p className="text-sm text-[#292828] font-medium">
                      {user.country}
                    </p>
                  </div>
                )}
                {user.city && (
                  <div>
                    <span className="text-xs text-[#9B919D]">City</span>
                    <p className="text-sm text-[#292828] font-medium">
                      {user.city}
                    </p>
                  </div>
                )}
                <div>
                  <span className="text-xs text-[#9B919D]">Role</span>
                  <p className="text-sm text-[#292828] font-medium">
                    {user.role || 'N/A'}
                  </p>
                </div>
                {user.gender && (
                  <div>
                    <span className="text-xs text-[#9B919D]">Gender</span>
                    <p className="text-sm text-[#292828] font-medium">
                      {user.gender}
                    </p>
                  </div>
                )}
                {user.occupation && (
                  <div>
                    <span className="text-xs text-[#9B919D]">Occupation</span>
                    <p className="text-sm text-[#292828] font-medium">
                      {user.occupation}
                    </p>
                  </div>
                )}
                <div>
                  <span className="text-xs text-[#9B919D]">Auth Provider</span>
                  <p className="text-sm text-[#292828] font-medium">
                    {user.authProvider || 'N/A'}
                  </p>
                </div>
                {user.brand && (
                  <div>
                    <span className="text-xs text-[#9B919D]">Brand</span>
                    <p className="text-sm text-[#292828] font-medium">
                      {user.brand}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {statsData.map((stat, index) => (
            <div
              key={index}
              className="bg-white border border-[#E5E9EB] rounded-3xl p-[24px] flex flex-col items-center justify-center gap-3"
            >
              <img
                src={stat.icon}
                alt={stat.label}
                className="w-[32px] h-[32px]"
              />
              <div className="text-center">
                <div className="text-2xl font-semibold text-[#191B1C]">
                  {stat.value}
                </div>
                <div className="text-xs text-[#9B919D] mt-1">{stat.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Links Table */}
        <div className="bg-white border border-[#E5E9EB] rounded-3xl p-[24px]">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-[#292828]">
              User Links ({filteredLinks.length})
            </h3>
            <input
              type="text"
              value={linkSearchTerm}
              onChange={(e) => setLinkSearchTerm(e.target.value)}
              placeholder="Search links..."
              className="border border-[#E5E9EB] rounded-2xl px-4 py-2 text-sm outline-none focus:border-[#3C2479] w-[300px]"
            />
          </div>

          <div className="overflow-x-auto rounded-2xl border border-gray-200">
            <table className="min-w-full table-auto">
              <thead>
                <tr className="bg-gray-100">
                  <th
                    className="font-semibold text-sm px-4 py-3 text-right cursor-pointer hover:bg-gray-200"
                    onClick={() =>
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
                    }
                  >
                    Clicks {sortOrder === 'asc' ? '↑' : '↓'}
                  </th>
                  <th className="font-semibold text-sm px-4 py-3 text-center">
                    Target Site
                  </th>
                  <th className="font-semibold text-sm px-4 py-3 text-center">
                    Short URL
                  </th>
                  <th className="font-semibold text-sm px-4 py-3 text-left">
                    Original URL
                  </th>
                  <th className="font-semibold text-sm px-4 py-3 text-center">
                    Meta Info
                  </th>
                  <th className="font-semibold text-sm px-4 py-3 text-center">
                    Tags
                  </th>
                  <th className="font-semibold text-sm px-4 py-3 text-center">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {paginatedLinks.length > 0 ? (
                  paginatedLinks.map((link) => {
                    // Build the complete short URL
                    const buildShortUrl = () => {
                      let url = '';

                      // Add protocol
                      url += 'https://';

                      // Add subdomain if exists
                      if (link.subDomain) {
                        url += `${link.subDomain}.`;
                      }

                      // Add domain
                      url += link.domain;

                      // Add prefix if exists
                      if (link.prefix) {
                        url += `/${link.prefix}`;
                      }

                      // Add short URL
                      url += `/${link.shortUrl}`;

                      return url;
                    };

                    const fullShortUrl = buildShortUrl();

                    // Display format for short URL
                    const displayShortUrl = () => {
                      let display = '';
                      if (link.subDomain) display += `${link.subDomain}.`;
                      display += link.domain;
                      if (link.prefix) display += `/${link.prefix}`;
                      display += `/${link.shortUrl}`;
                      return display;
                    };

                    return (
                      <tr
                        key={link._id}
                        className="bg-white hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 py-3 text-sm text-right text-gray-900 font-medium">
                          {link.clickCount}
                          {link.isFavorite && (
                            <span className="ml-2" title="Favorite">
                              ⭐
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-center text-gray-900">
                          {link.targetSite}
                        </td>
                        <td className="px-4 py-3 text-sm text-center">
                          <a
                            href={fullShortUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#1FA1FF] hover:underline"
                            title={fullShortUrl}
                          >
                            {displayShortUrl()}
                          </a>
                        </td>
                        <td className="px-4 py-3 text-sm max-w-[300px]">
                          <a
                            href={link.originalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#1FA1FF] hover:underline truncate block"
                            title={link.originalUrl}
                          >
                            {link.originalUrl}
                          </a>
                        </td>
                        <td className="px-4 py-3 text-sm text-center max-w-[200px]">
                          {link.metaTitle ? (
                            <div className="flex flex-col gap-1">
                              <span
                                className="font-medium text-gray-900 truncate block"
                                title={link.metaTitle}
                              >
                                {link.metaTitle}
                              </span>
                              {link.metaDescription && (
                                <span
                                  className="text-xs text-gray-500 truncate block"
                                  title={link.metaDescription}
                                >
                                  {link.metaDescription}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs">
                              No meta
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-center">
                          {link.tags && link.tags.length > 0 ? (
                            <div className="flex flex-wrap gap-1 justify-center">
                              {link.tags.slice(0, 2).map((tag, idx) => (
                                <span
                                  key={idx}
                                  className={`px-2 py-1 rounded-full text-xs ${ADMIN_POSITIVE_CHIP}`}
                                >
                                  {tag}
                                </span>
                              ))}
                              {link.tags.length > 2 && (
                                <span
                                  className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs"
                                  title={link.tags.join(', ')}
                                >
                                  +{link.tags.length - 2}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs">
                              No tags
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-center text-gray-600">
                          {formatDisplayDate(link.createdAt)}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-8 text-center text-gray-500"
                    >
                      {linkSearchTerm
                        ? 'No links found matching your search'
                        : 'This user has no links yet'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-6">
              <div className="text-sm text-[#9B919D]">
                Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                {Math.min(currentPage * itemsPerPage, sortedLinks.length)} of{' '}
                {sortedLinks.length} links
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-[#E5E9EB] rounded-lg text-sm font-medium text-[#292828] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="px-4 py-2 text-sm text-[#9B919D]">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border border-[#E5E9EB] rounded-lg text-sm font-medium text-[#292828] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminUserDetail;
